import {BigCalendarEvent, BigCalendar, BigCalendarPopupProps} from 'platform/big-calendar';
import {Alert, DataStatus} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {noop} from 'ramda-adjunct';

import {useEditTaskMutation} from '@dms/api/core';
import {useUpdateTestDriveMutation} from '@dms/api/sales';
import {openCreateTaskFormDialog, openEditTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {getCreateTaskDefaultValues} from '../../../../utils/getCreateTaskDefaultValues';
import {OutlookEventPopup} from './components/OutlookEventPopup/OutlookEventPopup';
import {useBigCalendarControl} from './hooks/useBigCalendarControl';
import {getBigCalendarDataFromCalendarEvents} from './utils/getBigCalendarDataFromCalendarEvents';
import {getBigCalendarDataFromGrid} from './utils/getBigCalendarDataFromGrid';
import {getBigCalendarDataFromTestDriveGrid} from './utils/getBigCalendarDataFromTestDriveGrid';
import {getTaskFromBigCalendarEvent} from './utils/getTaskFromBigCalendarEvent';
import {isOutlookCalendarEvent} from './utils/isOutlookCalendarEvent';
import {isTestDriveEvent} from './utils/isTestDriveEvent';

const TASK_CALENDAR_START_HOUR = 7;

export function TaskCalendar() {
  const navigate = useNavigate();

  const [canCreateTask, canEditTestDrive] = usePermissions({
    permissionKeys: ['createTask', 'editTestDrive'],
  });

  const control = useBigCalendarControl();

  const [editTask] = useEditTaskMutation();
  const [updateTestDrive] = useUpdateTestDriveMutation();

  const taskEvents = getBigCalendarDataFromGrid(control.tasks, control.selectedUsers);
  const testDriveEvents = getBigCalendarDataFromTestDriveGrid(
    control.testDrives,
    control.selectedUsers
  );
  const outlookEvents = getBigCalendarDataFromCalendarEvents(
    control.calendarEvents,
    control.selectedUsers
  );
  const allEvents = [...(taskEvents ?? []), ...(testDriveEvents ?? []), ...(outlookEvents ?? [])];

  const onEventDragMove = (event: BigCalendarEvent, start: Date, end: Date) =>
    match(event)
      // Outlook events are a read-only mirror; they are moved in Outlook, never here. The
      // platform already blocks the drag on `isReadOnly`, this is the second line of defence.
      .when(isOutlookCalendarEvent, () => Promise.resolve())
      .when(isTestDriveEvent, async (testDriveEvent) => {
        if (!canEditTestDrive || !testDriveEvent.testDriveData) {
          return;
        }

        await updateTestDrive({
          testDriveId: testDriveEvent.id,
          updateTestDriveRequestBody: {
            userId: testDriveEvent.testDriveData.userId,
            state: testDriveEvent.testDriveData.state,
            note: testDriveEvent.testDriveData.note,
            scheduledAt: start.toISOString(),
            returnedAt: testDriveEvent.testDriveData.hasReturnedAt ? end.toISOString() : null,
          },
        })
          .unwrap()
          .catch(handleApiError);
      })
      .otherwise(async (taskEvent) => {
        const editTaskRequestBody = getTaskFromBigCalendarEvent({
          event: taskEvent,
          newStart: start,
          newEnd: end,
          dataGridData: control.tasks,
        });

        if (!editTaskRequestBody) {
          return;
        }

        await editTask({
          id: taskEvent.id,
          editTaskRequestBody,
        }).unwrap();
      });

  const onDateClick = (date: Date) => {
    if (!canCreateTask) {
      return null;
    }
    openCreateTaskFormDialog({
      'data-testid': testIds.taskManager.taskCalendar('createTaskDialog'),
      values: getCreateTaskDefaultValues(date),
    });
  };

  const onEventClick = (event: BigCalendarEvent) =>
    match(event)
      // Read-only: everything an Outlook event has to show lives in the hover popup.
      .when(isOutlookCalendarEvent, noop)
      .when(isTestDriveEvent, (testDriveEvent) => {
        if (!testDriveEvent.businessCaseId) {
          return;
        }

        navigate(
          composePath(businessCaseRoutes.testDrive, {
            params: {id: testDriveEvent.businessCaseId},
            queryParams: {editTestDrive: testDriveEvent.id},
          })
        );
      })
      .otherwise((taskEvent) => {
        openEditTaskFormDialog({
          id: taskEvent.id,
          'data-testid': testIds.taskManager.taskCalendar('editTaskDialog'),
        });
      });

  const renderEventPopup = (popupProps: BigCalendarPopupProps) =>
    match(popupProps.event)
      .when(isOutlookCalendarEvent, (outlookEvent) => (
        <OutlookEventPopup
          data-testid={testIds.taskManager.taskCalendar('outlookEventPopup')}
          event={outlookEvent}
          anchorElement={popupProps.anchorElement}
          isVisible={popupProps.isVisible}
          users={control.users}
        />
      ))
      .otherwise(() => null);

  return (
    <DataStatus
      data={control.currentUser ? control.selectedUsers : null}
      isLoading={control.isLoading}
      isError={control.isError}
      minHeight={150}
    >
      {({data}) => (
        <VStack height="100%" spacing={2}>
          {/* Outlook events failing must not blank the calendar — tasks and test drives stay. */}
          <Show when={control.isCalendarEventsErrored}>
            <Alert
              data-testid={testIds.taskManager.taskCalendar('calendarEventsError')}
              variant="error"
              message={i18n.t('page.taskManager.notifications.calendarEventsLoadFailed')}
            />
          </Show>
          <Box flex={1} minHeight={0}>
            <BigCalendar
              enableHover
              data-testid={testIds.taskManager.taskCalendar('tasksCalendar')}
              defaultView="week"
              startHourView={TASK_CALENDAR_START_HOUR}
              events={allEvents}
              users={control.users}
              currentUser={control.currentUser!}
              selectedUsers={data}
              currentDate={control.selectedDate}
              onNavigate={control.onNavigate}
              onDateClick={onDateClick}
              onEventClick={onEventClick}
              onEventDragMove={onEventDragMove}
              onUserControlChange={control.onUserControlChange}
              renderEventPopup={renderEventPopup}
            />
          </Box>
        </VStack>
      )}
    </DataStatus>
  );
}
